import React, { useState, Dispatch, SetStateAction } from "react";

import Grid from "@mui/material/Grid";
import { Participant as IParticipant, RemoteParticipant } from "twilio-video";

import useAccessControlRoom from "../../hooks/useAccessControlRoom/useAccessControlRoom";
import useCustomerAllowRecord from "../../hooks/useCustomerAllowRecord/useCustomerAllowRecord";
import useLocalVideoToggle from "../../hooks/useLocalVideoToggle/useLocalVideoToggle";
import useMessages from "../../hooks/useMessages/useMessages";
import useParticipants from "../../hooks/useParticipants/useParticipants";
import useScreenShareParticipant from "../../hooks/useScreenShareParticipant/useScreenShareParticipant";
import useVideoContext from "../../hooks/useVideoContext/useVideoContext";
import Participant from "../../Participant/Participant";
import CommunicationSection from "../CommunicationSection";
import LocalParticipantActions from "./LocalParticipantActions";
import { profileStyle } from "./styles";
import TabComponent from "./TabsComponent";

interface Props {
  isCollapsed: boolean;
  setIsCollapsed: Dispatch<SetStateAction<boolean>>;
  currentTab: number;
  setCurrentTab: (value: number) => void;
}

interface AccessProps {
  participant: IParticipant;
}

const ParticipantAccess = ({ participant }: AccessProps) => {
  const { room } = useVideoContext();
  const { localParticipant } = room!;

  if (participant !== localParticipant) {
    useAccessControlRoom(participant as RemoteParticipant);
    useCustomerAllowRecord(participant);
  }

  useMessages(participant);

  return null;
};

const TabSection = ({ currentTab, isCollapsed, setCurrentTab, setIsCollapsed }: Props) => {
  const { room, isSharingScreen } = useVideoContext();
  const { localParticipant } = room!;
  const participants = useParticipants();
  const screenShareParticipant = useScreenShareParticipant();
  const [isVideoEnabled] = useLocalVideoToggle();

  const classes = profileStyle(isVideoEnabled, isSharingScreen)();

  const [showCommunication, setShowCommunication] = useState<boolean>(false);

  const setTabHeaderAction = (value: boolean, index?: number) => {
    if (index !== undefined) setCurrentTab(index);
    setIsCollapsed(!value);
    setShowCommunication(false);
  };

  if (isCollapsed)
    return (
      <>
        {participants.map((participant, index: number) => {
          return <ParticipantAccess key={`participant-${index}`} participant={participant} />;
        })}
      </>
    );

  return (
    <Grid container item xs={3} direction="column">
      <Grid item>
        {showCommunication ? (
          <CommunicationSection toggle={showCommunication} actions={setTabHeaderAction} />
        ) : (
          <TabComponent
            setIsCollapsed={setIsCollapsed}
            currentTab={currentTab}
            setCurrentTab={setCurrentTab}
          />
        )}
      </Grid>
      <Grid item>
        <div className={classes.profileSection}>
          {participants.map((participant, index: number) => {
            return <ParticipantAccess key={`participant-${index}`} participant={participant} />;
          })}
          <Participant
            key={localParticipant.sid}
            participant={localParticipant}
            isLocalParticipant={true}
            enableScreenShare={screenShareParticipant === localParticipant}
          />
          <LocalParticipantActions
            toggle={showCommunication}
            setToggle={() => setShowCommunication(!showCommunication)}
          />
        </div>
      </Grid>
    </Grid>
  );
};

export default TabSection;
